import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FaLock, FaEnvelope, FaEye, FaEyeSlash } from "react-icons/fa";
import logo from "../assets/tribe25_logo.png";
import { URLS } from "../utils/urls";
import api, { setAccessToken } from "../utils/api";
import { useTopLoader } from "../context/TopLoaderContext";

// Card component
const Card = ({ title, children }) => {
  return (
    <div className="relative flex flex-col items-center justify-center w-4/5 max-w-[350px] bg-white p-6 md:p-8 pt-12 rounded-xl shadow-lg text-center transition-all duration-300 ease-in-out">
      <img
        src={logo}
        alt="Logo"
        className="absolute -top-14 w-24 h-24 rounded-full border-[10px] border-white object-cover"
      />
      <h2 className="text-xl font-bold text-gray-800 mb-6">ADMIN</h2>
      {children}
    </div>
  );
};

// Input component
const Input = ({ type, placeholder, icon: Icon, value, onChange, required, children }) => {
  return (
    <div className="relative flex w-full mb-4">
      <div className="absolute inset-y-0 left-3 flex items-center text-gray-400">
        <Icon className="w-5 h-5" />
      </div>
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full pl-10 pr-3 py-3 rounded-lg bg-gray-100 text-gray-800 text-base focus:outline-none focus:ring-1 focus:ring-[#B53B74] transition duration-300"
      />
      {children}
    </div>
  );
};

const SignIn = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const { start, stop } = useTopLoader();

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const token = query.get("token");
    const error = query.get("error");

    if (token) {
      localStorage.setItem("token", token);
      setMessage("Login successful!");
      navigate("/admin/dashboard");
    } else if (error) {
      setMessage("Google sign-in failed. Please try again.");
    }
  }, [location, navigate]);

  // Normal login
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setLoading(true);
    start();
    try {
      const { data } = await api.post(URLS.LOGIN, { email, password });
      setAccessToken(data.accessToken);
      localStorage.setItem("userId", data.userId);
      localStorage.setItem("role", data.role);
      localStorage.setItem("firstName", data.firstName);
      localStorage.setItem("lastName", data.lastName);
      localStorage.setItem("token", data.accessToken);
      console.log("user ID:", data.userId);
      console.log("Role:", data.role);
      console.log("First Name:", data.firstName);
      console.log("Last Name:", data.lastName);
      setMessage("Login successful!");
      navigate("/admin/dashboard");
    } catch (err) {
      console.error(err);
      setMessage(err.response?.data?.message || "Login failed.");
    } finally {
      setLoading(false);
      stop();
    }
  };

  // Forgot password flow
  const handleForgotPassword = async () => {
    if (!email) {
      setMessage("Please enter your email first.");
      return;
    }

    setMessage("");
    setResetLoading(true);

    try {
      const { data } = await api.post(URLS.FORGOTPASSWORD, { email });
      setMessage(data.message || "A new password has been sent to your email.");
    } catch (err) {
      console.error(err);
      setMessage(err.response?.data?.message || "Password reset failed.");
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen w-full bg-[#DFDFDF] px-4">
      <Card>
        <form onSubmit={handleSubmit} className="w-full">
          {/* Email */}
          <Input
            type="email"
            placeholder="john@email.com"
            icon={FaEnvelope}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />

          {/* Password */}
          <div className="relative flex w-full mb-4">
            <div className="absolute inset-y-0 left-3 flex items-center text-gray-400">
              <FaLock className="w-5 h-5" />
            </div>
            <input
              type={showPassword ? "text" : "password"}
              placeholder="********"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full pl-10 pr-10 py-3 rounded-lg bg-gray-100 text-gray-800 text-base focus:outline-none focus:ring-1 focus:ring-[#B53B74] transition duration-300"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-3 flex items-center text-gray-400"
            >
              {showPassword ? <FaEyeSlash className="w-5 h-5" /> : <FaEye className="w-5 h-5" />}
            </button>
          </div>

          {/* Sign In button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#db8654] text-white py-3 rounded-lg font-bold shadow-md transition duration-200 hover:bg-[#8C134E] disabled:bg-pink-300 disabled:cursor-not-allowed"
          >
            {loading ? "Signing in..." : "Admin Sign in"}
          </button>
        </form>

        {/* Forgot password link */}
        <button
          type="button"
          onClick={handleForgotPassword}
          disabled={resetLoading}
          className="mt-4 text-sm font-medium text-[#B53B74] hover:underline disabled:text-gray-400"
        >
          {resetLoading ? "Sending reset link..." : "Forgot Password?"}
        </button>

        {message && <p className="text-sm text-center text-[#B53B74] mt-3">{message}</p>}
      </Card>
    </div>
  );
};

export default SignIn;
